import ApplicationController from './application_controller'

// IncidentV2 creation modal cascade: Step tab → Type radio → Reason button.
// For reasoned types only incident_v2[incident_reason_id] is submitted and the
// type is derived server-side. Reasonless types (data-reasonless) have no Reason
// step, so the chosen type is submitted via incident_v2[incident_type_id].
// (Tabs/radios/buttons layout; not the select-based admin cascade.)
export default class extends ApplicationController {
  static targets = ['tab', 'stepPanel', 'typeRadio', 'reasonGroup', 'reasonButton', 'reasonInput', 'typeInput']

  connect () {
    // A refused submit comes back re-rendered with the chosen type and reason
    // still in the hidden fields. Read them before activateStep clears them, so
    // the selection can be replayed and the operator only has to fix what was
    // actually wrong instead of walking the cascade again.
    const submitted = { typeId: this.typeInputTarget.value, reasonId: this.reasonInputTarget.value }

    const firstEnabled = this.tabTargets.find((tab) => !tab.disabled)
    if (firstEnabled) this.activateStep(firstEnabled.dataset.stepId)

    this.restoreSelection(submitted)
  }

  // Re-applies a submitted (type, reason) pair: the type is the submitted one
  // for a reasonless type, otherwise the one owning the submitted reason.
  restoreSelection ({ typeId, reasonId }) {
    const reasonButton = reasonId
      ? this.reasonButtonTargets.find((button) => button.dataset.reasonId === reasonId)
      : null
    const ownerTypeId = reasonButton
      ? reasonButton.closest('[data-incident-v2-form-target="reasonGroup"]')?.dataset.typeId
      : typeId
    if (!ownerTypeId) return

    const radio = this.typeRadioTargets.find((target) => target.dataset.typeId === ownerTypeId)
    if (!radio) return

    const panel = radio.closest('[data-incident-v2-form-target="stepPanel"]')
    if (panel) this.activateStep(panel.dataset.stepId)

    radio.checked = true
    this.applyType(radio)
    if (reasonButton) this.applyReason(reasonButton)
  }

  selectStep (event) {
    event.preventDefault()
    if (event.currentTarget.disabled) return

    this.activateStep(event.currentTarget.dataset.stepId)
  }

  activateStep (stepId) {
    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.stepId === stepId
      tab.classList.toggle('bg-blue-600', active)
      tab.classList.toggle('text-white', active)
      if (tab.disabled) return

      tab.classList.toggle('border-blue-600', active)
      tab.classList.toggle('border-gray-300', !active)
    })
    this.stepPanelTargets.forEach((panel) => {
      panel.classList.toggle('hidden', panel.dataset.stepId !== stepId)
    })
    this.typeRadioTargets.forEach((radio) => { radio.checked = false })
    this.reasonGroupTargets.forEach((group) => group.classList.add('hidden'))
    this.clearReason()
    this.typeInputTarget.value = ''
  }

  selectType (event) {
    this.applyType(event.currentTarget)
  }

  applyType (radio) {
    const typeId = radio.dataset.typeId
    const reasonless = radio.dataset.reasonless === 'true'
    this.reasonGroupTargets.forEach((group) => {
      group.classList.toggle('hidden', group.dataset.typeId !== typeId)
    })
    this.clearReason()
    // Reasonless types submit the type directly; reasoned types leave it blank
    // and let the server derive the type from the chosen reason.
    this.typeInputTarget.value = reasonless ? typeId : ''
  }

  selectReason (event) {
    event.preventDefault()
    this.applyReason(event.currentTarget)
  }

  applyReason (reasonButton) {
    this.reasonInputTarget.value = reasonButton.dataset.reasonId
    this.reasonButtonTargets.forEach((button) => {
      const active = button === reasonButton
      button.classList.toggle('bg-blue-600', active)
      button.classList.toggle('text-white', active)
      button.classList.toggle('border-blue-600', active)
      button.classList.toggle('border-gray-300', !active)
    })
  }

  clearReason () {
    this.reasonInputTarget.value = ''
    this.reasonButtonTargets.forEach((button) => {
      button.classList.remove('bg-blue-600', 'text-white', 'border-blue-600')
      button.classList.add('border-gray-300')
    })
  }
}
